<template>
  <div class="pi-overlay" @click.self="$emit('close')">
    <div class="pi-modal">
      <header class="pi-head">
        <div>
          <h3>AI Writer · Prompt Inspector</h3>
          <p class="pi-sub">
            Every LLM prompt used in this session - system prompt, user prompt and the model's response.
          </p>
        </div>
        <button class="pi-close" @click="$emit('close')" aria-label="Close">✕</button>
      </header>

      <div class="pi-body">
        <div v-if="!log.length" class="pi-empty">
          <p>No prompts captured yet.</p>
          <p class="pi-empty-sub">
            Run a step (generate a title, build the draft, regenerate a section, fix issues…) and the
            prompts will appear here as they come back.
          </p>
        </div>

        <div v-else>
          <div class="pi-summary">
            {{ log.length }} LLM call{{ log.length === 1 ? '' : 's' }} captured
            <span class="pi-summary-tokens">
              · {{ totals.input.toLocaleString() }} in · {{ totals.output.toLocaleString() }} out
              · {{ (totals.input + totals.output).toLocaleString() }} total tokens
            </span>
            <span v-if="totals.cost > 0" class="pi-summary-cost">· ~{{ fmtCost(totals.cost) }} est. cost</span>
          </div>

          <section v-for="group in groups" :key="group.section" class="pi-group">
            <h4 class="pi-group-title">{{ group.label }} <span class="pi-count">{{ group.calls.length }}</span></h4>

            <article v-for="call in group.calls" :key="call.uid" class="pi-call">
              <div class="pi-call-head">
                <span class="pi-call-name">{{ promptLabel(call.promptName) }}</span>
                <span class="pi-meta">{{ call.model || '-' }}</span>
                <span class="pi-meta">in {{ call.inputTokens || 0 }} · out {{ call.outputTokens || 0 }} tok</span>
                <span class="pi-meta" v-if="call.costUsd > 0">~{{ fmtCost(call.costUsd) }}</span>
                <span class="pi-meta" v-if="call.latencyMs != null">{{ call.latencyMs }} ms</span>
              </div>

              <div
                v-for="pane in panes"
                :key="pane.key"
                class="pi-pane"
              >
                <button class="pi-pane-head" @click="toggle(call.uid, pane.key)">
                  <span class="pi-caret" :class="{ open: isOpen(call.uid, pane.key) }">▸</span>
                  {{ pane.label }}
                  <span class="pi-pane-len">{{ (call[pane.field] || '').length }} chars</span>
                  <button
                    class="pi-copy"
                    @click.stop="copy(call.uid + '|' + pane.key, call[pane.field])"
                  >{{ copiedKey === (call.uid + '|' + pane.key) ? 'Copied' : 'Copy' }}</button>
                </button>
                <!-- Mounted only when expanded: prompts are large and repeated many times per run. -->
                <pre v-if="isOpen(call.uid, pane.key)" class="pi-pre">{{ call[pane.field] || '(empty)' }}</pre>
              </div>
            </article>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Admin-only modal. Renders the LLM prompts/responses accumulated in the Vuex
// store (`aiwPromptLog`) from AI Writer action responses — no fetching of its own.
const SECTION_LABELS = {
  ai_writer_plan_contract: "Planning · Editorial strategy",
  ai_writer_plan_competitors: "Planning · Competitor insights",
  ai_writer_plan_blueprint: "Planning · Page blueprint",
  ai_writer_plan_terms: "Planning · Term budget",
  ai_writer_plan_research: "Planning · Research map",
  ai_writer_title: "Title",
  ai_writer_structure: "Structure",
  ai_writer_structure_review: "Structure · Review",
  ai_writer_draft: "Draft",
  ai_writer_table_plan: "Draft · Tables",
  ai_writer_visual: "Draft · Visuals",
  ai_writer_internal_links: "Draft · Internal links",
  ai_writer_block_regen: "Block regenerate",
  ai_writer_image: "Images",
  ai_writer_quality: "SEO & Quality · Review",
  ai_writer_fix: "SEO & Quality · Fixes",
};
const PROMPT_LABELS = {
  content_contract: "Editorial strategy",
  competitor_insights: "Competitor insights",
  page_blueprint: "Page blueprint",
  term_budget: "Term budget",
  research_mapper: "Research map",
  title_generator: "Generate titles",
  title_refine: "Refine title",
  structure_planner: "Plan structure",
  intro_block: "Intro",
  h2_cluster_writer: "Write H2 cluster",
  existing_content_optimizer: "Optimize H2 cluster",
  table_planner: "Plan table",
  table_generator: "Generate table",
  list_generator: "Generate list",
  faq_generator: "Generate FAQ",
  key_highlights: "Key highlights",
  brief_overview_generator: "Brief overview",
  citation_block_generator: "Sources",
  table_of_contents_generator: "Table of contents",
  internal_link_placer: "Place internal links",
  block_regenerate: "Regenerate block",
  image_brief: "Image brief",
  image_alt_text: "Image name + alt text",
  quality_judge: "Quality review",
  seo_reconciliation: "Reconcile SEO terms",
  editorial_polish: "Editorial polish",
  auto_fix: "Auto-fix issue",
};

export default {
  name: "PromptInspectorModal",
  data() {
    return {
      expanded: {},
      copiedKey: "",
      panes: [
        { key: "system", label: "System prompt", field: "system" },
        { key: "user", label: "User prompt", field: "user" },
        { key: "response", label: "Response", field: "response" },
      ],
    };
  },
  computed: {
    log() {
      return this.$store.state.aiwPromptLog || [];
    },
    totals() {
      return this.log.reduce(
        (acc, c) => ({
          input: acc.input + (c.inputTokens || 0),
          output: acc.output + (c.outputTokens || 0),
          cost: acc.cost + (c.costUsd || 0),
        }),
        { input: 0, output: 0, cost: 0 }
      );
    },
    groups() {
      const map = new Map();
      this.log.forEach((c) => {
        const key = c.section || "other";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(c);
      });
      return Array.from(map.entries()).map(([section, calls]) => ({
        section,
        label: SECTION_LABELS[section] || section,
        calls,
      }));
    },
  },
  methods: {
    promptLabel(name) {
      return PROMPT_LABELS[name] || name || "Prompt";
    },
    fmtCost(v) {
      if (!v) return "$0";
      if (v >= 1) return "$" + v.toFixed(2);
      if (v >= 0.01) return "$" + v.toFixed(4);
      return "$" + v.toFixed(6);
    },
    _k(uid, pane) {
      return uid + "|" + pane;
    },
    isOpen(uid, pane) {
      return !!this.expanded[this._k(uid, pane)];
    },
    toggle(uid, pane) {
      const k = this._k(uid, pane);
      this.$set(this.expanded, k, !this.expanded[k]);
    },
    async copy(key, text) {
      try {
        await navigator.clipboard.writeText(text || "");
        this.copiedKey = key;
        setTimeout(() => { if (this.copiedKey === key) this.copiedKey = ""; }, 1200);
      } catch (e) { /* clipboard unavailable */ }
    },
  },
};
</script>

<style scoped>
.pi-overlay {
  position: fixed; inset: 0;
  background: rgba(5, 46, 51, 0.45);
  z-index: 200;
  display: flex; align-items: center; justify-content: center;
  padding: 32px;
  font-family: "Mulish", system-ui, sans-serif;
}
.pi-modal {
  background: var(--surface, #fff);
  border-radius: 14px;
  width: 100%; max-width: 920px;
  max-height: 86vh;
  display: flex; flex-direction: column;
  box-shadow: 0 18px 60px rgba(15, 20, 25, 0.22);
  overflow: hidden;
}
.pi-head {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; padding: 20px 24px;
  border-bottom: 1px solid var(--border, #e4e4e4);
}
.pi-head h3 { margin: 0; font-size: 17px; font-weight: 800; color: var(--text, #052e33); }
.pi-sub { margin: 4px 0 0; font-size: 12.5px; color: var(--text-3, #6b7b7e); }
.pi-close {
  background: transparent; border: none; cursor: pointer;
  font-size: 16px; color: var(--text-3, #6b7b7e); padding: 4px 8px; border-radius: 6px;
}
.pi-close:hover { background: var(--surface-2, #f7f7f7); color: var(--text, #052e33); }

.pi-body { padding: 18px 24px 24px; overflow-y: auto; }
.pi-summary { font-size: 12px; font-weight: 700; color: var(--text-3, #6b7b7e); margin-bottom: 14px; text-transform: uppercase; letter-spacing: 0.04em; }
.pi-summary-tokens { color: var(--text-3, #9aa6a8); font-weight: 600; }
.pi-summary-cost { color: var(--pop-green, #25a97a); font-weight: 700; }

.pi-empty { text-align: center; padding: 48px 20px; color: var(--text-2, #014751); }
.pi-empty p { margin: 0; font-weight: 700; }
.pi-empty-sub { margin-top: 8px !important; font-weight: 400 !important; font-size: 13px; color: var(--text-3, #6b7b7e); }

.pi-group { margin-bottom: 22px; }
.pi-group-title {
  margin: 0 0 10px; font-size: 14px; font-weight: 800; color: var(--text, #052e33);
  display: flex; align-items: center; gap: 8px;
}
.pi-count {
  font-size: 11px; font-weight: 700; color: var(--text-3, #6b7b7e);
  background: var(--surface-2, #f1f3f3); border-radius: 999px; padding: 1px 8px;
}

.pi-call { border: 1px solid var(--border, #e4e4e4); border-radius: 10px; margin-bottom: 10px; overflow: hidden; }
.pi-call-head {
  display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
  padding: 10px 14px; background: var(--surface-2, #f7f9f9);
  border-bottom: 1px solid var(--border, #e4e4e4);
}
.pi-call-name { font-weight: 700; font-size: 13px; color: var(--text, #052e33); }
.pi-meta { font-size: 11.5px; color: var(--text-3, #6b7b7e); }

.pi-pane { border-top: 1px solid var(--border, #eee); }
.pi-pane:first-of-type { border-top: none; }
.pi-pane-head {
  width: 100%; display: flex; align-items: center; gap: 8px;
  background: transparent; border: none; cursor: pointer;
  padding: 9px 14px; font-size: 12.5px; font-weight: 600; color: var(--text-2, #014751);
  font-family: inherit; text-align: left;
}
.pi-pane-head:hover { background: var(--surface-2, #f7f9f9); }
.pi-caret { display: inline-block; transition: transform 0.15s; color: var(--text-3, #6b7b7e); }
.pi-caret.open { transform: rotate(90deg); }
.pi-pane-len { color: var(--text-3, #9aa6a8); font-weight: 400; margin-left: 2px; }
.pi-copy {
  margin-left: auto; background: transparent; border: 1px solid var(--border, #e4e4e4);
  border-radius: 6px; padding: 3px 10px; font-size: 11px; font-weight: 600;
  color: var(--text-3, #6b7b7e); cursor: pointer; font-family: inherit;
}
.pi-copy:hover { color: var(--text, #052e33); border-color: var(--text-3, #6b7b7e); }
.pi-pre {
  margin: 0; padding: 12px 16px; background: #0f1419; color: #e7edee;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, monospace;
  font-size: 12px; line-height: 1.5; white-space: pre-wrap; word-break: break-word;
  max-height: 360px; overflow-y: auto;
}
</style>
